import { TrackingPageSettings } from "@redotech/redo-model/order-tracking/tracking-page-definition";
import { memo } from "react";
import { Flex } from "../../../flex";
import { LoadingRedoAnimation } from "../../../loading-redo-animation";
import { TrackingPageEnvironment } from "../../context/tracking-page-context";
import { getBlockStyles, getScrollbarHiddenStyles } from "./block-styles";

interface BlockWrapperProps {
  pageSettings: TrackingPageSettings | undefined;
  environment: TrackingPageEnvironment;
  loading?: boolean;
  children: React.ReactNode;
  className?: string;
  withBorder?: boolean;
  withScrollbar?: boolean;
  transparentBackground?: boolean;
  inert?: boolean;
}

/**
 * Common wrapper for tracking page blocks with consistent styling
 */
export const BlockWrapper = memo(function BlockWrapper({
  pageSettings,
  environment,
  loading = false,
  children,
  className,
  withBorder = true,
  withScrollbar = false,
  transparentBackground = false,
  inert,
}: BlockWrapperProps) {
  const baseStyles = getBlockStyles(pageSettings);
  const scrollStyles = withScrollbar ? getScrollbarHiddenStyles() : {};
  const finalStyles = transparentBackground
    ? { ...baseStyles, backgroundColor: "transparent" }
    : baseStyles;

  const borderStyle = withBorder
    ? { border: `1px solid ${baseStyles.borderColor ?? "#d6d6d6"}` }
    : {};

  if (loading) {
    return (
      <Flex
        align="center"
        className={className}
        dir="column"
        flex={1}
        gap="lg"
        justify="center"
        p="3xl"
        style={{ ...finalStyles, ...borderStyle }}
      >
        <LoadingRedoAnimation />
      </Flex>
    );
  }

  return (
    <Flex
      className={className}
      dir="column"
      flex={1}
      gap="xl"
      inert={inert ?? environment === TrackingPageEnvironment.BUILDER}
      overflow="hidden"
      style={{ ...finalStyles, ...scrollStyles, ...borderStyle }}
    >
      {children}
    </Flex>
  );
});
